import React, { useState } from "react";
import { Link } from "react-router-dom";
import Header from "../components/Header";
import Footer from "../components/Footer";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "../components/ui/accordion";
import { HelpCircle, Phone, MessageCircle, Search, Mail } from "lucide-react";
import { FAQS } from "../mock/mock";

const FAQPage = () => {
  const [query, setQuery] = useState("");

  const filtered = FAQS.map((cat) => ({
    ...cat,
    items: cat.items.filter(
      (i) =>
        !query ||
        i.q.toLowerCase().includes(query.toLowerCase()) ||
        i.a.toLowerCase().includes(query.toLowerCase())
    ),
  })).filter((c) => c.items.length > 0);

  return (
    <>
      <Header />
      <main>
        <section className="relative pt-40 pb-20 md:pt-48 md:pb-24 overflow-hidden">
          <img
            src="https://images.unsplash.com/photo-1450101499163-c8848c66ca85?w=1920&q=80"
            alt=""
            className="absolute inset-0 w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-slate-950/85 to-slate-900/50" />
          <div className="relative max-w-7xl mx-auto px-6 text-white">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white/10 backdrop-blur border border-white/20 text-white text-xs font-semibold tracking-wider uppercase">
              <HelpCircle className="w-3.5 h-3.5" /> Frequently Asked Questions
            </div>
            <h1 className="mt-4 text-4xl md:text-6xl font-bold tracking-tight max-w-3xl" style={{ fontFamily: "'Poppins', sans-serif" }}>
              We're here to help
            </h1>
            <p className="mt-4 text-white/85 text-lg max-w-2xl">
              Everything you need to know about bookings, cancellations, safety, and more. Can't find your answer? We're a call away.
            </p>
            <div className="mt-8 max-w-xl relative">
              <Search className="w-5 h-5 absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" />
              <Input
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search FAQs..."
                className="pl-11 h-12 bg-white/95 text-slate-900 border-transparent focus-visible:ring-orange-400"
              />
            </div>
          </div>
        </section>

        <section className="py-16 md:py-20 bg-slate-50">
          <div className="max-w-4xl mx-auto px-6 space-y-8">
            {filtered.length === 0 && (
              <div className="bg-white p-10 rounded-2xl border border-slate-200 text-center">
                <HelpCircle className="w-12 h-12 text-slate-300 mx-auto" />
                <p className="mt-4 text-slate-600">No FAQs match your search. Try different keywords or contact us directly.</p>
              </div>
            )}

            {filtered.map((cat) => (
              <div key={cat.category} className="bg-white p-6 md:p-8 rounded-2xl border border-slate-200 shadow-sm">
                <h2 className="text-xl md:text-2xl font-bold text-slate-900" style={{ fontFamily: "'Poppins', sans-serif" }}>
                  {cat.category}
                </h2>
                <Accordion type="single" collapsible className="mt-4">
                  {cat.items.map((item, i) => (
                    <AccordionItem key={i} value={`${cat.category}-${i}`}>
                      <AccordionTrigger className="text-left text-slate-900 font-semibold hover:text-teal-700 hover:no-underline">
                        {item.q}
                      </AccordionTrigger>
                      <AccordionContent className="text-slate-600 leading-relaxed">
                        {item.a}
                      </AccordionContent>
                    </AccordionItem>
                  ))}
                </Accordion>
              </div>
            ))}

            {/* Still need help */}
            <div className="bg-gradient-to-r from-teal-700 to-teal-600 text-white p-6 md:p-8 rounded-2xl">
              <div className="flex items-center gap-3">
                <MessageCircle className="w-6 h-6" />
                <h3 className="text-2xl font-bold" style={{ fontFamily: "'Poppins', sans-serif" }}>Still have questions?</h3>
              </div>
              <p className="mt-2 text-white/85">Our travel experts are just a call or click away — 24/7.</p>
              <div className="mt-5 flex flex-wrap gap-3">
                <Button asChild className="bg-white text-teal-700 hover:bg-slate-100 h-11">
                  <a href="tel:+919397363140"><Phone className="w-4 h-4 mr-2" /> Call +91 9397363140</a>
                </Button>
                <Button asChild variant="outline" className="border-white text-white hover:bg-white hover:text-teal-700 bg-white/10 backdrop-blur h-11">
                  <a href="mailto:bookings@pmrtravels.com"><Mail className="w-4 h-4 mr-2" /> Email Us</a>
                </Button>
                <Button asChild variant="outline" className="border-white text-white hover:bg-white hover:text-teal-700 bg-white/10 backdrop-blur h-11">
                  <Link to="/contact">Contact Form</Link>
                </Button>
              </div>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
};

export default FAQPage;
